import { GenericTable } from '@ui-components/Table/GenericTable'
import React, { useEffect, useMemo, useState } from 'react'
import { Maintenance, maintenanceColumns, Outages, outagesColumns } from './OutagesData';
import { useAppSelector } from '@redux/hooks';
import { getFailoverHistoryAPI, getMonitoringAPI } from '../requests/request';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { GenericFilter } from '@ui-components/Filter/GenericFilter';
import { useProcessingOverlay } from '@shared/hooks/useProcessingOverlay';
import SegmentedControl from '@ui-components/SegmentedContol/GenericSegmentedContol';
import GenericCard from '@ui-components/Card/GenericCard';
import { useLocation } from 'react-router-dom';

export default function OutagesView() {
  const api = useAxiosAuth0();
  const location = useLocation();

  const [isFetching, setIsFetching] = useState(false);
  const overlaySpinner = useProcessingOverlay(isFetching);
  const [outagesList, setOutagesList] = useState<Outages[]>([]);
  const [maintenanceList, setMaintenanceList] = useState<Maintenance []>([]);

  // const tenantId = useAppSelector((state) => state.tenant.id);
  const { tenantId } = useAppSelector(state => state.selection);
  //Filters logic
  const [searchColumn, setSearchColumn] = useState("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const initialSelectedView = location.state?.selectedView ?? "outages";
  const [selectedView, setSelectedView] = useState<"outages" | "maintenance">(initialSelectedView);
  const itemToRender = [
    { label: "Outages", value: "outages" },
    { label: "Maintenance", value: "maintenance" },
  ];

    useEffect(() => {
      async function fetchData() {
        try {
          setIsFetching(true);
          const [failoverResp, maintenanceResp] = await Promise.all([
            getFailoverHistoryAPI(api, tenantId),
            getMonitoringAPI(api, tenantId),
          ]);
          if (failoverResp && Array.isArray(failoverResp.data.data.failoverHistory)) {
            const shapedOutages = failoverResp.data.data.failoverHistory.map((p: Outages) => ({
              date: p.date,
              description: p.description,
              outageDuration: formatDuration(p.outageDuration),
              eventId: p.eventId,
            }));
            setOutagesList(shapedOutages);
          }
          if (maintenanceResp && Array.isArray(maintenanceResp.data.data)) {
            const shapedMaintenance = maintenanceResp.data.data.map((p: Outages) => ({
              date: p.date,
              description: p.description,
              outageDuration: formatDuration(p.outageDuration),
              eventId: p.eventId,
            }));
            setMaintenanceList(shapedMaintenance);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsFetching(false);
        }
      }
      fetchData();
    }, [api]);
    

  //Helper converter seconds
  function formatDuration(duration: number): string {
    if (duration < 60) {
    return `${duration} sec.`;
    }
    const minutes = duration / 60;
    if (minutes < 60) {
    return `${Math.round(minutes)} min.`;
    }
    const hours = minutes / 60;
    return `${Math.round(hours)} hr${Math.round(hours) > 1 ? "s" : ""}.`;
  }

  // Filter the data according to the selected item
    const filteredData = useMemo(() => {
      // Select the correct list based on the selected view.
      const list = selectedView === "outages" ? outagesList : maintenanceList;
    
      // Filter the list using the search column and search term.
      return list.filter((item) => {
        if (searchColumn === "All") {
          return (
            item.date.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        // Otherwise, filter by specific column
        switch (searchColumn) {
          case "Date":
            return item.date.toLowerCase().includes(searchTerm.toLowerCase());
          case "Description":
            return item.description.toLowerCase().includes(searchTerm.toLowerCase());
          default:
            return true;
        }
      });
    }, [selectedView, outagesList, maintenanceList, searchColumn, searchTerm]);
  

  return (
    <div>
        {overlaySpinner}
        <GenericCard 
          headerContent={
          <SegmentedControl
            options={itemToRender}
            value={selectedView}
            onChange={(value) => setSelectedView(value as "outages" | "maintenance")}
            size="small"
          />}
          isCollapsible={false}
        >
        <div className="flex justify-end mb-5">
          <GenericFilter
            searchColumn={searchColumn}
            onSearchColumnChange={setSearchColumn}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            columnOptions={[
              "All",
              "Description",
              "Date",
            ]}
          />
        </div>
        {selectedView === "outages" ? (
          <GenericTable
            columns={outagesColumns}
            data={filteredData}
            initialRowsPerPage={5}
            rowsPerPageOptions={[5, 10]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
          />
        ) : (
          <GenericTable
            columns={maintenanceColumns}
            data={filteredData}
            initialRowsPerPage={5}
            rowsPerPageOptions={[5, 10]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
          />
        )}
        </GenericCard>
    </div>
  )
}
